/**
 * Shared checkout API functions
 * Used by both widget and widgetsv4 apps
 */

import axios, { AxiosError } from "axios";
import { codeExcludesAddOns } from "@obby/lib";
import { isWebsiteV2Checkout } from "./websiteV2Channel";
import type {
  CheckoutApiConfig,
  CourseWithBundlesResponse,
  CourseDatesResponse,
  SessionData,
  LoadMoreDatesResult,
  CreateCheckoutSessionData,
  CreateCheckoutSessionResponse,
  FreePurchaseResponse,
  DiscountResult,
  AvailableSlotsResponse,
  SourceInfo,
} from "./types";
import type {
  CheckoutParticipantField,
  ExistingParticipant,
} from "../../components/checkout/participants-step";
import type { CheckoutCustomerField } from "../../components/checkout/customer-fields";

const DEFAULT_API_URL = "https://api.baluu.io";

// Exported for sibling checkout fetchers (pool-availability.ts) so every
// network call resolves the host identically across widget/webcomponents/youmo.
export function getBaseUrl(config: CheckoutApiConfig): string {
  return config.baseUrl || DEFAULT_API_URL;
}

function getWindowLocation() {
  if (typeof window === "undefined") {
    return {
      href: "",
      host: "",
      protocol: "",
      hostname: "",
    };
  }
  return {
    href: window.location.href,
    host: window.location.host,
    protocol: window.location.protocol,
    hostname: window.location.hostname,
  };
}

/**
 * Detects if running inside an iframe
 */
function isInIframe(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.parent !== window;
  } catch {
    // Cross-origin check may throw, assume iframe
    return true;
  }
}

/**
 * Detects if the embed query parameter is present
 */
function hasEmbedQueryParam(): boolean {
  if (typeof window === "undefined") return false;
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.has("embed");
}

/**
 * A checkout on a Website v2 page, or on a hosted flow its links opened,
 * priced as one (services/pricing/serviceFeeChannels.ts). It wins over
 * iframe/webcomponent detection on purpose: a checkout ON a websiteV2 page
 * is a websiteV2 checkout whatever embed technology renders it. False while
 * that channel isn't priced as its own (./websiteV2Channel).
 */
export function hasWebsiteV2Marker(): boolean {
  if (typeof window === "undefined") return false;
  return isWebsiteV2Checkout(window.location.search);
}

/**
 * Gets the parent URL when in an iframe
 */
function getParentUrl(): string | undefined {
  if (typeof window === "undefined" || !isInIframe()) {
    return undefined;
  }

  try {
    const referrerUrl = new URL(document.referrer);
    return `${referrerUrl.origin}${referrerUrl.pathname}`;
  } catch {
    return document.referrer || undefined;
  }
}

/**
 * Gets comprehensive source information for purchase tracking
 * @param isWebcomponent - If true, marks source as webcomponent
 *
 * Priority:
 * 0. A Website v2 checkout → websiteV2, while that channel is priced as its
 *    own (./websiteV2Channel); it wins over whatever embed technology
 *    renders the checkout
 * 1. If isWebcomponent → webcomponent
 * 2. If ?embed= query param exists → embedded
 * 3. If in iframe → embedded
 * 4. Otherwise → live_baluu (direct widget access)
 */
export function getSourceInfo(isWebcomponent = false): SourceInfo {
  if (typeof window === "undefined") {
    return {
      sourceType: isWebcomponent ? "webcomponent" : "embedded",
      url: "",
      isIframe: false,
    };
  }

  const inIframe = isInIframe();
  const currentUrl = window.location.href;
  const hostname = window.location.hostname;
  const referrer = document.referrer || undefined;
  const parentUrl = getParentUrl();

  // Determine source type based on priority
  let sourceType: SourceInfo["sourceType"];
  if (hasWebsiteV2Marker()) {
    sourceType = "websiteV2";
  } else if (isWebcomponent) {
    sourceType = "webcomponent";
  } else if (hasEmbedQueryParam() || inIframe) {
    sourceType = "embedded";
  } else {
    sourceType = "live_baluu";
  }

  return {
    sourceType,
    url: currentUrl,
    parentUrl,
    referrer,
    isIframe: inIframe,
    hostname,
  };
}

function handleApiError(
  error: unknown,
  config: CheckoutApiConfig,
  context: Record<string, unknown>,
): never {
  const axiosError = error as AxiosError<{ message?: string }>;
  const errorMessage =
    axiosError.response?.data?.message ||
    axiosError.message ||
    "Something went wrong";

  const err = new Error(errorMessage);

  if (config.onError) {
    config.onError(err, context);
  }

  throw err;
}

/**
 * Fetch course data with bundles
 */
export async function fetchCourseWithBundles(
  courseId: string,
  config: CheckoutApiConfig,
): Promise<CourseWithBundlesResponse> {
  const baseUrl = getBaseUrl(config);

  try {
    const response = await axios.get<CourseWithBundlesResponse>(
      `${baseUrl}/widget/courses/${courseId}/with-bundles`,
      {
        params: {
          earlyAccess: config.earlyAccess,
          buyerEmail: config.buyerEmail,
        },
      },
    );
    return response.data;
  } catch (error) {
    return handleApiError(error, config, {
      action: "fetchCourseWithBundles",
      courseId,
    });
  }
}

/**
 * Fetch the listing's checkout-eligible participant (attendee) field
 * definitions — public endpoint, no auth. Fields resolve per attendance
 * profile: when `courseId` is provided, the API resolves that course's
 * profile override (falling back to the school default), rather than
 * always returning the school-wide set. Deliberately NEVER throws: the
 * orchestrator awaits this inside its loading gate, and a failure must
 * degrade the checkout to legacy guest questions (fields = []), not block it.
 */
export async function fetchParticipantFields(
  schoolId: string,
  courseId: string | undefined,
  config: CheckoutApiConfig,
): Promise<CheckoutParticipantField[]> {
  const baseUrl = getBaseUrl(config);
  try {
    const response = await axios.get<{ data: CheckoutParticipantField[] }>(
      `${baseUrl}/widget/participant-fields`,
      { params: courseId ? { schoolId, courseId } : { schoolId }, timeout: 3000 },
    );
    return Array.isArray(response.data?.data) ? response.data.data : [];
  } catch (error) {
    // Fail-open (never throw) — but give hosts a breadcrumb so a failing
    // endpoint (attendee step silently vanishing) is diagnosable.
    try {
      config.onError?.(error instanceof Error ? error : new Error(String(error)), {
        action: "fetchParticipantFields",
        schoolId,
      });
    } catch {
      // host onError must never break the fail-open contract
    }
    return [];
  }
}

export type CheckoutBuyerConfig = {
  phone: { askAtCheckout: boolean; isRequired: boolean };
};

/** Defaults for a stale API that predates the `buyer` key: what every checkout
 *  did unconditionally before the built-in phone row went live. */
const DEFAULT_BUYER_CONFIG: CheckoutBuyerConfig = {
  phone: { askAtCheckout: true, isRequired: true },
};

/**
 * Fetch the buyer-facing custom customer-field DEFINITIONS (never customer
 * data) for the given courses — public endpoint, no auth. `courseIds` scopes
 * which "specific"-scope fields apply; an empty list resolves the school-wide
 * ones only, exactly as a course-less basket does. Also resolves the school's
 * built-in buyer config (currently just the phone row) alongside the custom
 * field definitions, since both come off the same endpoint.
 *
 * Fail-open like `fetchParticipantFields`: a failure must degrade the surface
 * to "no custom fields" + the pre-existing unconditional-phone default, never
 * block it. Note the basket checkout does NOT use this helper — it tracks its
 * own idle/loaded/failed status because it has to block PAYMENT on a failed
 * load, a stricter contract than fail-open.
 */
export async function fetchCheckoutCustomerFields(
  schoolId: string,
  courseIds: string[],
  config: CheckoutApiConfig,
): Promise<{ fields: CheckoutCustomerField[]; buyer: CheckoutBuyerConfig }> {
  const baseUrl = getBaseUrl(config);
  try {
    const response = await axios.get<{ data: CheckoutCustomerField[]; buyer?: CheckoutBuyerConfig }>(
      `${baseUrl}/widget/customer-fields`,
      { params: { schoolId, courseIds: courseIds.join(",") }, timeout: 3000 },
    );
    return {
      fields: Array.isArray(response.data?.data) ? response.data.data : [],
      buyer: response.data?.buyer ?? DEFAULT_BUYER_CONFIG,
    };
  } catch (error) {
    try {
      config.onError?.(error instanceof Error ? error : new Error(String(error)), {
        action: "fetchCheckoutCustomerFields",
        schoolId,
      });
    } catch {
      // host onError must never break the fail-open contract
    }
    return { fields: [], buyer: DEFAULT_BUYER_CONFIG };
  }
}

/** Wire shape of `GET /widget/participants` — `values` is the joined-row
 * array `listParticipants` returns server-side (`{slug,label,value,fieldType,
 * isRequired}[]`), NOT the slug-keyed `Record<string,string>` the UI type
 * declares. `fetchExistingParticipants` maps array → record at this boundary
 * so every downstream reader (`missingRequiredForSaved`, the saved-row
 * completion form) can trust `ExistingParticipant.values[slug]`. */
type ExistingParticipantWire = {
  id: string;
  isSelf?: boolean;
  displayName: string;
  values: { slug: string; value: string }[];
};

/**
 * Fetch the signed-in customer's saved attendees (session-gated endpoint).
 * Bearer token in the Authorization header; deliberately NO credentials mode
 * (the /widget/* routes serve wildcard CORS — credentialed requests are
 * browser-blocked). Fail-open: resolves [] on any failure.
 */
export async function fetchExistingParticipants(
  schoolId: string,
  bearerToken: string | null | undefined,
  config: CheckoutApiConfig,
): Promise<ExistingParticipant[]> {
  const baseUrl = getBaseUrl(config);
  try {
    const response = await axios.get<{ data: ExistingParticipantWire[] }>(
      `${baseUrl}/widget/participants`,
      {
        params: { schoolId },
        timeout: 3000,
        headers: bearerToken
          ? { Authorization: `Bearer ${bearerToken}` }
          : undefined,
      },
    );
    const rows = Array.isArray(response.data?.data) ? response.data.data : [];
    return rows.map((p) => ({
      id: p.id,
      isSelf: p.isSelf === true,
      displayName: p.displayName,
      values: Object.fromEntries((p.values ?? []).map((v) => [v.slug, v.value])),
    }));
  } catch (error) {
    // Fail-open (never throw) — the step still allows manual entry. Emit the
    // host breadcrumb so a failing endpoint is diagnosable.
    try {
      config.onError?.(error instanceof Error ? error : new Error(String(error)), {
        action: "fetchExistingParticipants",
        schoolId,
      });
    } catch {
      // host onError must never break the fail-open contract
    }
    return [];
  }
}

/**
 * Fetch course dates/sessions
 */
export async function fetchCourseDates(
  courseId: string,
  config: CheckoutApiConfig,
  page: number = 1,
  options?: {
    earlyAccess?: string;
    isBundle?: boolean;
    bundleID?: string;
    limit?: number;
    /** When provided, backend should ensure this session is included in the response */
    sessionId?: string;
    /** ISO date — only sessions starting on/after this date are returned */
    fromDate?: string;
    /** PartnerLocation id — only sessions at this location are returned */
    locationId?: string;
    /** Waiting-list claim-link hold id — exempts the claimer's own held seats from the sold-out display */
    offer?: string;
  },
): Promise<CourseDatesResponse> {
  const baseUrl = getBaseUrl(config);

  try {
    const response = await axios.get<SessionData[]>(
      `${baseUrl}/widget/courses/${courseId}/dates`,
      {
        params: {
          page,
          limit: options?.limit,
          earlyAccess: options?.earlyAccess,
          isBundle: options?.isBundle,
          bundleID: options?.bundleID,
          sessionId: options?.sessionId,
          fromDate: options?.fromDate,
          locationId: options?.locationId,
          offer: options?.offer,
        },
      },
    );
    return response.data;
  } catch (error) {
    return handleApiError(error, config, {
      action: "fetchCourseDates",
      courseId,
      page,
    });
  }
}

/**
 * Load more dates helper - returns sessions with hasMore flag
 */
export async function loadMoreDates(
  courseId: string,
  config: CheckoutApiConfig,
  { page, limit }: { page: number; limit: number },
  options?: {
    earlyAccess?: string;
    isBundle?: boolean;
    bundleID?: string;
    /** When provided on initial load, backend should ensure this session is included */
    sessionId?: string;
    /** ISO date — only sessions starting on/after this date are returned */
    fromDate?: string;
    /** PartnerLocation id — only sessions at this location are returned */
    locationId?: string;
    /** Waiting-list claim-link hold id — exempts the claimer's own held seats from the sold-out display */
    offer?: string;
  },
): Promise<LoadMoreDatesResult> {
  const sessions = await fetchCourseDates(courseId, config, page, {
    ...options,
    limit,
  });

  return {
    sessions,
    hasMore: sessions.length >= limit,
  };
}

/**
 * Fetch term dates for term checkout
 * This returns terms (events) with a representative session per term,
 * NOT individual sessions within a term.
 */
export async function fetchTermDates(
  courseId: string,
  config: CheckoutApiConfig,
  page: number = 1,
  options?: {
    earlyAccess?: string;
    limit?: number;
    /** When provided, backend should ensure this session is included in the response */
    sessionId?: string;
    /** Waiting-list claim-link token (hold id or offerGroupId) — exempts the
     * claimer's own held seats from the sold-out display */
    offer?: string;
  },
): Promise<SessionData[]> {
  const baseUrl = getBaseUrl(config);

  try {
    const response = await axios.get<SessionData[]>(
      `${baseUrl}/widget/courses/${courseId}/term-dates`,
      {
        params: {
          page,
          limit: options?.limit,
          earlyAccess: options?.earlyAccess,
          sessionId: options?.sessionId,
          offer: options?.offer,
        },
      },
    );
    return response.data;
  } catch (error) {
    return handleApiError(error, config, {
      action: "fetchTermDates",
      courseId,
      page,
    });
  }
}

/**
 * Load more term dates helper - returns terms with hasMore flag
 */
export async function loadMoreTermDates(
  courseId: string,
  config: CheckoutApiConfig,
  { page, limit }: { page: number; limit: number },
  options?: {
    earlyAccess?: string;
    /** When provided on initial load, backend should ensure this session is included */
    sessionId?: string;
    /** Waiting-list claim-link token — exempts the claimer's own held seats */
    offer?: string;
  },
): Promise<LoadMoreDatesResult> {
  const sessions = await fetchTermDates(courseId, config, page, {
    ...options,
    limit,
  });

  return {
    sessions,
    hasMore: sessions.length >= limit,
  };
}

/**
 * Fetch trial dates for trial checkout
 * Only returns sessions from terms that have isTrialEnabled = true
 */
export async function fetchTrialDates(
  courseId: string,
  config: CheckoutApiConfig,
  page: number = 1,
  options?: {
    earlyAccess?: string;
    limit?: number;
    sessionId?: string;
  },
): Promise<SessionData[]> {
  const baseUrl = getBaseUrl(config);

  try {
    const response = await axios.get<{ sessions: SessionData[]; ticketTypes: any[] }>(
      `${baseUrl}/widget/courses/${courseId}/trial-dates`,
      {
        params: {
          page,
          limit: options?.limit,
          earlyAccess: options?.earlyAccess,
          sessionId: options?.sessionId,
        },
      },
    );
    // API returns { sessions, ticketTypes } - extract just the sessions
    return response.data.sessions || [];
  } catch (error) {
    return handleApiError(error, config, {
      action: "fetchTrialDates",
      courseId,
      page,
    });
  }
}

/**
 * Load more trial dates helper
 */
export async function loadMoreTrialDates(
  courseId: string,
  config: CheckoutApiConfig,
  { page, limit }: { page: number; limit: number },
  options?: {
    earlyAccess?: string;
    sessionId?: string;
  },
): Promise<LoadMoreDatesResult> {
  const sessions = await fetchTrialDates(courseId, config, page, {
    ...options,
    limit,
  });

  return {
    sessions,
    hasMore: sessions.length >= limit,
  };
}

/**
 * Fetch drop-in dates for drop-in checkout
 * Only returns sessions from terms that have isDropInEnabled = true
 */
export async function fetchDropInDates(
  courseId: string,
  config: CheckoutApiConfig,
  page: number = 1,
  options?: {
    earlyAccess?: string;
    limit?: number;
    sessionId?: string;
  },
): Promise<SessionData[]> {
  const baseUrl = getBaseUrl(config);

  try {
    const response = await axios.get<{ sessions: SessionData[]; ticketTypes: any[] }>(
      `${baseUrl}/widget/courses/${courseId}/drop-in-dates`,
      {
        params: {
          page,
          limit: options?.limit,
          earlyAccess: options?.earlyAccess,
          sessionId: options?.sessionId,
        },
      },
    );
    // API returns { sessions, ticketTypes } - extract just the sessions
    return response.data.sessions || [];
  } catch (error) {
    return handleApiError(error, config, {
      action: "fetchDropInDates",
      courseId,
      page,
    });
  }
}

/**
 * Load more drop-in dates helper
 */
export async function loadMoreDropInDates(
  courseId: string,
  config: CheckoutApiConfig,
  { page, limit }: { page: number; limit: number },
  options?: {
    earlyAccess?: string;
    sessionId?: string;
  },
): Promise<LoadMoreDatesResult> {
  const sessions = await fetchDropInDates(courseId, config, page, {
    ...options,
    limit,
  });

  return {
    sessions,
    hasMore: sessions.length >= limit,
  };
}

/**
 * Fetch available slots for appointments or group schedule (private bookings)
 */
export async function fetchAvailableSlots(
  courseId: string,
  month: number,
  year: number,
  config: CheckoutApiConfig,
  bookingType?: string,
  ticketTypeId?: string,
  locationId?: string,
  /**
   * Ticket ids to compute equipment caps for. A SEPARATE axis from
   * ticketTypeId, which on a variable-duration listing decides which slots
   * exist at all. Joined to CSV rather than passed as an array: axios would
   * serialise an array as poolTicketTypeIds[]=a&poolTicketTypeIds[]=b, which
   * the server rejects as a non-string.
   */
  poolTicketTypeIds?: string[],
): Promise<AvailableSlotsResponse> {
  const baseUrl = getBaseUrl(config);

  // Convert month/year to startDate/endDate format that API expects
  // Month is 1-indexed (1 = January)
  const startDate = `${year}-${String(month).padStart(2, "0")}-01`;
  const lastDay = new Date(year, month, 0).getDate();
  const endDate = `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;

  try {
    const response = await axios.get(
      `${baseUrl}/widget/courses/${courseId}/available-slots`,
      {
        params: {
          startDate,
          endDate,
          bookingType,
          ticketTypeId,
          locationId,
          poolTicketTypeIds: poolTicketTypeIds?.length
            ? poolTicketTypeIds.join(",")
            : undefined,
        },
      },
    );

    // Transform API response to expected format
    // API returns: { availableDates: [{ dateISO, isDisabled, availableSlots }], locationsById }
    // Component expects: { dates: [...], slots: [...], locationsById }
    const apiData = response.data as any;
    return {
      dates: apiData.availableDates || [],
      slots:
        apiData.availableDates?.flatMap((d: any) =>
          (d.availableSlots || []).map((slot: any) => ({
            ...slot,
            dateISO: d.dateISO,
          })),
        ) || [],
      // Per-slot locationId already survives the spread above; locationsById
      // is the distinct-location name map built once by the API (display-only).
      locationsById: apiData.locationsById,
      locationOptions: apiData.locationOptions,
      hasMultipleLocations: apiData.hasMultipleLocations,
      locationSelectionMode: apiData.locationSelectionMode,
    };
  } catch (error: any) {
    console.warn("Failed to fetch available slots", error);
    return { dates: [], slots: [] };
  }
}

/**
 * Creates a checkout session which handles:
 * 1. Creating the order/booking
 * 2. Creating the Stripe payment intent
 * 3. Returning the client secret for payment
 */
export async function createCheckoutSession(
  data: CreateCheckoutSessionData,
  config: CheckoutApiConfig,
): Promise<CreateCheckoutSessionResponse> {
  const baseUrl = getBaseUrl(config);
  const location = getWindowLocation();

  // Get sourceInfo from config or generate it
  const sourceInfo = config.sourceInfo || getSourceInfo(config.isWebcomponent);

  const dataObj = data as Record<string, unknown>;
  const pricingSummary = dataObj._pricingSummary as
    | { netTotal?: number }
    | undefined;
  const netTotalPence =
    typeof pricingSummary?.netTotal === "number"
      ? pricingSummary.netTotal
      : undefined;

  const payload = {
    cancelURL: location.href,
    returnURL: location.href,
    returnHost: location.host,
    returnProtocol: location.protocol,
    source: config.source || "widget",
    sourceURL: location.href,
    sourceInfo,
    ...data,
    isFree:
      netTotalPence !== undefined
        ? netTotalPence === 0
        : Boolean(dataObj.isFree),
  };

  try {
    const response = await axios.post(
      `${baseUrl}/app/createCheckoutSession`,
      payload,
    );

    const {
      paymentIntentClientSecret,
      paymentMethodType,
      schoolAccountID,
      usedStripeHostedPage,
      checkoutURl,
      purchase,
    } = response.data;

    const result: CreateCheckoutSessionResponse = {
      clientSecret: paymentIntentClientSecret,
      paymentMethodType,
      schoolAccountID,
      usedStripeHostedPage: usedStripeHostedPage || false,
      checkoutUrl: checkoutURl || null,
      purchaseId: purchase?._id || null,
    };

    if (config.onAnalytics) {
      config.onAnalytics("checkout_session_created", {
        source: config.source,
        usedStripeHostedPage,
      });
    }

    return result;
  } catch (error) {
    return handleApiError(error, config, {
      action: "createCheckoutSession",
      source: config.source,
    });
  }
}

/**
 * Confirms a free booking by routing through the basket two-step:
 *   POST /app/createBasketCheckoutSession  → mints a BasketOrder (amount === 0)
 *   POST /app/confirmFreeBasketOrder       → dispatches Inngest item events
 *
 * When `data.bundleID` is present the order is a bundleV2 redemption: we
 * skip Step 1 and call /confirmFreeBasketOrder in Mode 2 with {items, buyer,
 * bundleId} — matches the basket-checkout-page flow at
 * pages/basket/checkout.tsx:1219-1234. Step 1 + bundleId would hit a 400
 * because ConfirmFreeBasketOrderController Mode 1 requires `!bundleId`.
 *
 * The legacy `processImmediately` option is accepted but ignored — the basket
 * flow always dispatches Inngest events. Returns once the BasketOrder is
 * confirmed; callers that need the downstream Booking / ProductPurchase id
 * should call `pollBasketOrderRecord` with the returned `basketOrderId`.
 */
export async function confirmFreeBooking(
  data: Record<string, unknown>,
  config: CheckoutApiConfig,
  _options?: { processImmediately?: boolean },
): Promise<FreePurchaseResponse> {
  const baseUrl = getBaseUrl(config);
  const sourceInfo = config.sourceInfo || getSourceInfo(config.isWebcomponent);

  // Translate the orchestrator's free-booking data shape into a single
  // BasketItem of type "booking". Accept both modern field names
  // (sessionId, sessionDate, currency) and legacy ones emitted by the
  // orchestrator hooks (sessionid, startDateTime, currencyUsed) so we work
  // against the actual data shape without forcing a hook refactor.
  const user = data.user as Record<string, any> | undefined;
  const school = data.school as Record<string, any> | undefined;
  const sessionId =
    (data.sessionId as string | undefined) ||
    (data.sessionid as string | undefined);
  const sessionDate =
    (data.sessionDate as string | undefined) ||
    (data.startDateTime as string | undefined);
  const currency =
    (data.currency as string | undefined) ||
    (data.currencyUsed as string | undefined) ||
    "GBP";
  const schoolId =
    (data.schoolId as string | undefined) ||
    (school?._id as string | undefined) ||
    "";
  const schoolName =
    (data.schoolName as string | undefined) ||
    (school?.name as string | undefined) ||
    "";
  const bundleId =
    (data.bundleID as string | undefined) ||
    (data.bundleId as string | undefined);

  const item = {
    id:
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? (crypto as Crypto).randomUUID()
        : `item_${Date.now()}_${Math.random().toString(36).slice(2)}`,
    type: "booking" as const,
    schoolId,
    schoolName,
    courseId: data.courseId as string | undefined,
    courseName: data.courseName as string | undefined,
    sessionId,
    sessionDate,
    bookingType:
      (data.bookingType as
        | "regular"
        | "dropIn"
        | "trial"
        | "term"
        | "appointment") || "regular",
    ticketSelections: data.ticketSelections as unknown[] | undefined,
    ticketVersion: data.ticketVersion as string | undefined,
    addOns: data.addOns as unknown[] | undefined,
    appointmentData: data.appointmentData as Record<string, unknown> | undefined,
    termData: data.termData as Record<string, unknown> | undefined,
    userInputs: data.userInputs as unknown[] | undefined,
    checkoutGuestsInformation: data.checkoutGuestsInformation as
      | unknown[]
      | undefined,
    guestDetails: data.guestDetails as Record<string, unknown> | undefined,
    quantity: 1,
    price: 0,
    currency,
  };

  const buyer = (data.buyer as Record<string, unknown>) || {
    email: user?.renderEmail,
    firstName: user?.profile?.firstName,
    lastName: user?.profile?.lastName,
    phone: user?.profile?.phone,
    userId: user?._id,
    marketingOptIn: data.marketingOptIn,
  };

  const source =
    config.source || (config.isWebcomponent ? "webcomponent" : "widget");

  try {
    let basketOrderId: string;

    if (bundleId) {
      // Bundle redemption path — Mode 2 of ConfirmFreeBasketOrderController.
      // Step 1 (/createBasketCheckoutSession) would be rejected by the
      // controller because Mode 1 requires `!bundleId`.
      const confirmRes = await axios.post(
        `${baseUrl}/app/confirmFreeBasketOrder`,
        {
          items: [item],
          buyer,
          discountCode: data.discountCode || undefined,
          source,
          sourceInfo,
          bundleId,
        },
      );
      basketOrderId = confirmRes.data.basketOrderId;
    } else {
      const sessionRes = await axios.post(
        `${baseUrl}/app/createBasketCheckoutSession`,
        {
          items: [item],
          buyer,
          discountCode: data.discountCode || undefined,
          source,
          sourceInfo,
        },
      );

      basketOrderId = sessionRes.data.basketOrderId;
      const amount: number = sessionRes.data.amount ?? 0;
      if (amount !== 0) {
        throw new Error(
          `confirmFreeBooking expected amount === 0, got ${amount}. Caller asserted free purchase.`,
        );
      }

      await axios.post(`${baseUrl}/app/confirmFreeBasketOrder`, {
        basketOrderId,
      });
    }

    if (config.onAnalytics) {
      config.onAnalytics("free_booking_confirmed", {
        source: config.source,
        basketOrderId,
      });
    }

    const result: FreePurchaseResponse = {
      // Keep purchaseID populated with basketOrderId for callers that still
      // read it. Callers that need the actual record id must poll via
      // pollBasketOrderRecord.
      purchase: { basketOrderId },
      purchaseID: basketOrderId,
      basketOrderId,
      success: true,
    };

    return result;
  } catch (error) {
    return handleApiError(error, config, {
      action: "confirmFreeBooking",
      source: config.source,
    });
  }
}

/**
 * Validate a discount code
 */
export async function validateDiscount(
  code: string,
  email: string,
  courseId: string,
  config: CheckoutApiConfig,
  options?: {
    schoolId?: string;
    typeCheck?: string;
  },
): Promise<DiscountResult> {
  const baseUrl = getBaseUrl(config);

  try {
    const response = await axios.post(`${baseUrl}/widget/discounts/validate`, {
      code,
      validateEmail: true,
      email,
      courseId,
      schoolId: options?.schoolId,
      ...(options?.typeCheck ? { typeCheck: options.typeCheck } : {}),
    });

    // API may return discount in different shapes
    const discount = response.data.discount || response.data;

    return {
      isValid: true,
      discountValue: discount?.value || discount?.discountValue,
      percentage: discount?.percentage,
      message: discount?.message,
      // Also include original API fields
      valid: true,
      value: discount?.value,
      type: discount?.type as "PERCENTAGE" | "PRICE" | undefined, // FIX: Include type from API response
      code: discount?.code,
      // DEV-327: gift cards / credit never exclude add-ons, whatever the doc says.
      excludeAddOns: codeExcludesAddOns(discount),
      discount,
    };
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string; error?: string }>;
    const errorMsg =
      axiosError.response?.data?.message ||
      axiosError.response?.data?.error ||
      "Invalid discount code";
    return {
      isValid: false,
      valid: false,
      message: errorMsg,
      error: errorMsg,
    };
  }
}

/**
 * Check order/purchase status after payment
 * Polls the backend to see if the order has been processed
 */
export type OrderStatusResult = {
  isReady: boolean;
  redirectUrl?: string;
  isError?: boolean;
  errorMessage?: string;
};

export async function checkOrderStatus(
  purchaseId: string,
  config: CheckoutApiConfig,
): Promise<OrderStatusResult> {
  const baseUrl = getBaseUrl(config);

  try {
    const response = await axios.get(
      `${baseUrl}/app/checkPaymentIntentStatus/${purchaseId}`,
    );

    const { isDataProcessed, returnURl, isError, errorData } = response.data;

    return {
      isReady: isDataProcessed === true,
      redirectUrl: returnURl,
      isError: isError,
      errorMessage: errorData?.ux_message,
    };
  } catch (error) {
    // On error, return not ready (will keep polling)
    return {
      isReady: false,
      isError: true,
      errorMessage: "Failed to check order status",
    };
  }
}

/**
 * Fetch available slots for multiple listings in parallel.
 * Returns a Map of listingId -> AvailableSlotsResponse.
 */
export async function fetchMultiListingAvailableSlots(
  courseIds: string[],
  month: number,
  year: number,
  config: CheckoutApiConfig,
  bookingType?: string,
): Promise<Map<string, AvailableSlotsResponse>> {
  const results = await Promise.all(
    courseIds.map(async (id) => {
      const slots = await fetchAvailableSlots(id, month, year, config, bookingType);
      return [id, slots] as const;
    }),
  );
  return new Map(results);
}

/**
 * Fetch course data with bundles for multiple listings in parallel.
 * Returns a Map of listingId -> CourseWithBundlesResponse.
 */
export async function fetchMultipleCourses(
  courseIds: string[],
  config: CheckoutApiConfig,
): Promise<Map<string, CourseWithBundlesResponse>> {
  const results = await Promise.allSettled(
    courseIds.map(async (id) => {
      const data = await fetchCourseWithBundles(id, config);
      return [id, data] as const;
    }),
  );

  const map = new Map<string, CourseWithBundlesResponse>();
  for (const result of results) {
    if (result.status === "fulfilled") {
      map.set(result.value[0], result.value[1]);
    }
  }
  return map;
}

// Re-export types for convenience
export type {
  CheckoutApiConfig,
  CourseWithBundlesResponse,
  CourseDatesResponse,
  SessionData,
  LoadMoreDatesResult,
  CreateCheckoutSessionData,
  CreateCheckoutSessionResponse,
  FreePurchaseResponse,
  DiscountResult,
  AvailableSlotsResponse,
  SourceInfo,
} from "./types";
